class ConfigService {
  constructor() {
    this._config = {};
    this._secureConfig = {};
    this._isLoaded = false;
    this._isSecureConfigLoaded = false;
  }

  async load() {
    if (this._isLoaded) {
      return;
    }

    try {
      const response = await fetch("/config.json");
      if (!response.ok) {
        throw new Error(`Failed to fetch config.json: ${response.statusText}`);
      }

      const data = await response.json();

      this._config = data;
      this._isLoaded = true;
    } catch (error) {
      console.error("CRITICAL: Could not load app configuration!", error);
      throw error;
    }
  }

  /**
   * Load secure configuration from the backend endpoint
   * This should be called after successful authentication
   * @param {Function} apiFunction - The API function to call (e.g., getAppConfig)
   */
  async loadSecureConfig(apiFunction) {
    try {
      const response = await apiFunction();

      if (response.data) {
        this._secureConfig = response.data;
        this._isSecureConfigLoaded = true;

        // Store in localStorage as backup for page reloads
        localStorage.setItem("secureConfig", JSON.stringify(response.data));

        console.log("Secure configuration loaded successfully");
      }
    } catch (error) {
      console.error("Failed to load secure configuration:", error);

      // Try to load from localStorage as fallback
      const stored = localStorage.getItem("secureConfig");
      if (stored) {
        try {
          this._secureConfig = JSON.parse(stored);
          this._isSecureConfigLoaded = true;
          console.log("Loaded secure configuration from localStorage");
        } catch (e) {
          console.error("Failed to parse stored secure config:", e);
        }
      }
    }
  }

  /**
   * Load secure config from localStorage if available
   * Used on app initialization for authenticated users
   */
  loadSecureConfigFromStorage() {
    const stored = localStorage.getItem("secureConfig");
    if (stored) {
      try {
        this._secureConfig = JSON.parse(stored);
        this._isSecureConfigLoaded = true;
        console.log("Loaded secure configuration from localStorage on init");
      } catch (e) {
        console.error("Failed to parse stored secure config:", e);
      }
    }
  }

  /**
   * Clear secure configuration (on logout)
   */
  clearSecureConfig() {
    this._secureConfig = {};
    this._isSecureConfigLoaded = false;
    localStorage.removeItem("secureConfig");
  }

  get(key) {
    if (!this._isLoaded) {
      console.warn(
        `Config service has not been loaded yet, but tried to access key: "${key}"`
      );
    }

    // Priority: secure config > public config
    // This allows secure values to override public ones
    if (this._isSecureConfigLoaded && key in this._secureConfig) {
      return this._secureConfig[key];
    }

    return this._config[key];
  }

  /**
   * Check if secure config is loaded
   */
  isSecureConfigLoaded() {
    return this._isSecureConfigLoaded;
  }
}

const instance = new ConfigService();
export default instance;
